import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AIChatbot from '@/components/AIChatbot';
import { Sparkles, TrendingUp, FileText, Shield, CheckCircle, Star, Menu, X, Play } from 'lucide-react';
import AuthModal from '@/components/AuthModal';
import { toast } from 'sonner';

const LandingPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleAuthSuccess = (token, user) => {
    login(token, user);
    toast.success('Welcome to FixMyScore AI!');
    navigate('/dashboard');
  };

  const features = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'AI Dispute Letters',
      description: 'Generate legally-compliant FCRA dispute letters in seconds with our advanced AI'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Credit Score Tracking',
      description: 'Monitor your credit scores from all 3 bureaus in real-time'
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Smart Analysis',
      description: 'AI-powered credit report analysis identifies errors and opportunities'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: '110% Money-Back',
      description: 'See improvement in 90 days or get 110% of your money back'
    }
  ];

  const testimonials = [
    { name: 'Sarah M.', text: 'My score went up 120 points in just 3 months!', rating: 5 },
    { name: 'James K.', text: 'The AI letter generator saved me thousands in credit repair fees', rating: 5 },
    { name: 'Maria L.', text: 'Finally got approved for my dream home. Thank you!', rating: 5 }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-slate-600 hover:text-violet-600 font-medium transition-colors duration-200">Features</a>
              <a href="#pricing" className="text-slate-600 hover:text-violet-600 font-medium transition-colors duration-200">Pricing</a>
              <a href="#testimonials" className="text-slate-600 hover:text-violet-600 font-medium transition-colors duration-200">Reviews</a>
              <Button 
                data-testid="login-button"
                variant="ghost" 
                onClick={() => { setAuthMode('login'); setShowAuth(true); }}
                className="font-heading font-semibold"
              >
                Login
              </Button>
              <Button 
                data-testid="get-started-button"
                onClick={() => { setAuthMode('register'); setShowAuth(true); }}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6 font-heading font-semibold shadow-lg shadow-violet-200 hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <a href="#features" className="block text-slate-600 hover:text-violet-600 font-medium">Features</a>
              <a href="#pricing" className="block text-slate-600 hover:text-violet-600 font-medium">Pricing</a>
              <a href="#testimonials" className="block text-slate-600 hover:text-violet-600 font-medium">Reviews</a>
              <Button variant="ghost" onClick={() => { setAuthMode('login'); setShowAuth(true); }} className="w-full">Login</Button>
              <Button onClick={() => { setAuthMode('register'); setShowAuth(true); }} className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full">Get Started</Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-100 via-fuchsia-50 to-slate-50 opacity-60"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {/* Large Logo for Hero Section */}
              <div className="mb-6">
                <img 
                  src="/disputeking-logo.png" 
                  alt="DisputeKing.AI"
                  className="h-80 w-auto mb-4"
                />
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-tight">
                The most advanced <span className="text-gradient">AI Credit Repair</span> tool ever created
              </h1>
              <p className="text-2xl sm:text-3xl font-heading font-semibold text-gradient mb-4">
                The confidence of a 700+ Score
              </p>
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
                The King of credit repair platforms. AI-powered do-it-yourself dispute generation, professional CRM for brokers, and tools that actually work. Join thousands taking control of their credit destiny.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  data-testid="hero-get-started"
                  onClick={() => { setAuthMode('register'); setShowAuth(true); }}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 py-6 text-lg font-heading font-semibold shadow-xl shadow-violet-300 hover:shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  Start Free Trial
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowVideoModal(true)}
                  className="rounded-full px-8 py-6 text-lg font-heading font-semibold border-2 border-violet-200 hover:border-violet-400 hover:bg-violet-50 transition-all duration-300"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Watch Demo
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div>
                  <div className="text-3xl font-heading font-bold text-violet-600">15,000+</div>
                  <div className="text-sm text-slate-600">Happy Users</div>
                </div>
                <div>
                  <div className="text-3xl font-heading font-bold text-violet-600">4.8★</div>
                  <div className="text-sm text-slate-600">User Rating</div>
                </div>
                <div>
                  <div className="text-3xl font-heading font-bold text-violet-600">120+</div>
                  <div className="text-sm text-slate-600">Avg Score Increase</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_fix-my-score-5/artifacts/wzc3t7rf_1000018868.png" 
                alt="The confidence of a 700+ Score"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4">Advanced AI Credit Repair Features</h2>
            <p className="text-lg text-slate-600">From dispute letters to progress tracking, AI handles the hard work</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white border-2 border-slate-100 rounded-3xl p-8 hover:border-violet-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-heading font-semibold mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4">How It Works</h2>
            <p className="text-lg text-slate-600">Get started in just 5 minutes</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-violet-600 text-white text-3xl font-heading font-bold flex items-center justify-center mx-auto mb-6">1</div>
              <h3 className="text-xl font-heading font-semibold mb-3">Sign Up & Connect</h3>
              <p className="text-slate-600">Create your account and connect to credit monitoring</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-violet-600 text-white text-3xl font-heading font-bold flex items-center justify-center mx-auto mb-6">2</div>
              <h3 className="text-xl font-heading font-semibold mb-3">AI Analyzes Your Credit</h3>
              <p className="text-slate-600">Our AI scans your report and identifies errors</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-violet-600 text-white text-3xl font-heading font-bold flex items-center justify-center mx-auto mb-6">3</div>
              <h3 className="text-xl font-heading font-semibold mb-3">Generate & Mail Disputes</h3>
              <p className="text-slate-600">One-click dispute generation and tracking</p>
            </div>
          </div>
          
          {/* Explainer Video */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-violet-100">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-heading font-bold mb-2">See DisputeKing.AI in Action</h3>
                <p className="text-slate-600">Watch how easy it is to repair your credit</p>
              </div>
              <div className="relative aspect-video bg-slate-900 rounded-2xl overflow-hidden shadow-2xl">
                <video 
                  controls 
                  className="w-full h-full"
                  poster="/logo.png"
                >
                  <source src="/explainer-video-smooth.mp4" type="video/mp4" />
                  <source src="/explainer-video-final.mp4" type="video/mp4" />
                  <source src="/demo-video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="mt-6 text-center">
                <Button
                  onClick={() => { setAuthMode('register'); setShowAuth(true); }}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 py-3 font-heading font-semibold shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Start Your Free Trial Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-lg text-slate-600">One plan, unlimited disputes</p>
          </div>
          <div className="bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-3xl p-8 text-white shadow-2xl">
            <div className="text-center">
              <h3 className="text-2xl font-heading font-bold mb-2">Pro Plan</h3>
              <div className="text-6xl font-heading font-bold mb-2">$59.99<span className="text-2xl">/mo</span></div>
              <p className="text-violet-100 mb-8">7-day free trial • Cancel anytime</p>
              <ul className="space-y-4 text-left max-w-md mx-auto mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0" />
                  <span>Unlimited AI dispute letter generation</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0" />
                  <span>3-Bureau credit monitoring</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0" />
                  <span>AI credit report analysis</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0" />
                  <span>Progress tracking dashboard</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0" />
                  <span>24/7 support</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0" />
                  <span>110% money-back guarantee</span>
                </li>
              </ul>
              <Button 
                data-testid="pricing-get-started"
                onClick={() => { setAuthMode('register'); setShowAuth(true); }}
                className="bg-white text-violet-600 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-heading font-semibold w-full max-w-md mx-auto hover:scale-105 transition-all duration-300"
              >
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4">Trusted by Thousands</h2>
            <p className="text-lg text-slate-600">See what our users are saying</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-4">"{testimonial.text}"</p>
                <p className="font-heading font-semibold text-violet-600">- {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">Ready to Fix Your Credit?</h2>
          <p className="text-xl mb-8 text-violet-100">Start your 7-day free trial today. No credit card required.</p>
          <Button 
            data-testid="cta-get-started"
            onClick={() => { setAuthMode('register'); setShowAuth(true); }}
            className="bg-white text-violet-600 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-heading font-semibold shadow-xl hover:scale-105 transition-all duration-300"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/disputeking-logo.png" 
                  alt="DisputeKing.AI"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-sm">AI-powered credit repair for everyone</p>
            </div>
            <div>
              <h4 className="text-white font-heading font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">How It Works</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-heading font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-heading font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/terms" className="hover:text-white transition-colors">Terms of Use</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">110% Guarantee</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center">
            <div className="max-w-4xl mx-auto mb-6 p-6 bg-slate-800 rounded-2xl">
              <h4 className="text-white font-heading font-semibold mb-3 text-lg">Important Disclaimer</h4>
              <p className="text-slate-300 text-sm leading-relaxed">
                DisputeKing.AI is <strong>not a credit repair company</strong>. We provide software tools and resources 
                that empower individuals and credit repair professionals to manage their own credit repair process. 
                Our platform offers AI-powered dispute generation, document management, and CRM capabilities designed 
                to simplify and streamline the credit repair workflow. Users maintain full control and responsibility 
                for their credit repair activities.
              </p>
            </div>
            <p className="text-sm">© 2026 DisputeKing.AI. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <AuthModal 
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        mode={authMode}
        onSuccess={handleAuthSuccess}
      />

      {/* Video Demo Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <div className="relative bg-black">
            <DialogHeader className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-6">
              <DialogTitle className="text-2xl font-heading font-bold text-white">
                See How DisputeKing.AI Works
              </DialogTitle>
            </DialogHeader>
            <video 
              className="w-full h-auto"
              controls
              autoPlay
              playsInline
              src="/demo-video.mp4"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </DialogContent>
      </Dialog>

      <AIChatbot user={null} />
    </div>
  );
};

export default LandingPage;